import {
	Alert,
	Button,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	Row,
	Select,
	notification,
} from 'antd';
import { FC, Fragment, useState } from 'react';
import { useFRMCustomerFormAlert, useFRMCustomerPage } from './CustomerPage';
import rule, { rules } from '@/libs/antd/rules';
import {
	useSelectAddress,
	useSelectBusinessEntity,
	useSelectCompanyType,
	useSelectCustomerPicPosition,
	useSelectIndustry,
} from '@/libs/fleet-api/hooks/master-data';
import { BusinessEntityType, PortfolioGuideline } from '@/libs/fleet-api/enums';
import { inputRtRw } from '@/libs/antd/input-number';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { formItem } from '@/libs/antd/form';
import { useApp } from '@/App';
import { getCustomers } from '@/libs/fleet-api/services/customer';
import { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import { useUserData } from '@/libs/fleet-api/hooks';

const FRMCustomerData: FC<{ onSubmit: () => void }> = ({ onSubmit }) => {
	const userData = useUserData();
	const { customerForm, customerDetail, isEdit, customerId } =
		useFRMCustomerPage();
	const { form, value, fieldName } = customerForm;
	const { navigate, queryClient } = useApp();

	const { customerInformation } = customerDetail ?? {};
	const selectBusinessEntity = useSelectBusinessEntity();
	const selectCompanyType = useSelectCompanyType();
	const selectAddress = useSelectAddress();
	const selectIndustry = useSelectIndustry();
	const selectPicPosition = useSelectCustomerPicPosition();

	const isPGSelective =
		value?.customerInformation?.industrySector?.portofolioGuideline ===
		PortfolioGuideline.SELECTIVE;

	const isPGWaspada =
		value?.customerInformation?.industrySector?.portofolioGuideline ===
		PortfolioGuideline.WASPADA;

	const [showDeviationModal, setShowDeviationModal] = useState(false);
	const [isDeviationFilled, setIsDeviationFilled] = useState<boolean>(
		!!customerInformation?.deviation,
	);

	const alerts = useFRMCustomerFormAlert('customerInformation');
	const [isCheckCustomer, setIsCheckCustomer] = useState<boolean>(!!customerId);

	const handleCheckCustomer = async (npwpNo?: string) => {
		if (!npwpNo || (npwpNo?.length ?? 0) < 15) {
			notification.warning({
				message: 'NPWP tidak valid',
				description: 'NPWP harus berjumlah minimal 15 digit',
			});
			return;
		}

		try {
			const queryParams: FleetQueryParams = {
				search: {
					by: 'npwp',
					value: npwpNo,
				},
				pagination: {
					pageNumber: 1,
					pageSize: 1,
				},
			};

			const response = await getCustomers(queryParams);
			const items = response.data?.items ?? [];

			if (items.length > 0 && items[0].customerId) {
				const foundCustomerId = items[0].customerId;
				const customerSource = items[0].source;

				const notificationKey = `customer-found-${foundCustomerId}`;

				if (customerSource === 'LOS') {
					if (items[0].createdById !== userData?.userId) {
						notification.warning({
							key: notificationKey,
							message: 'Customer ditemukan',
							description: (
								<>
									Customer merupakan kelolaan FRM lain, tidak bisa membuat
									customer dengan npwp ini
								</>
							),
						});
						return;
					} else {
						notification.success({
							key: notificationKey,
							message: 'Customer ditemukan',
							description: (
								<>
									Customer dengan NPWP <strong>{npwpNo}</strong> ditemukan dan
									merupakan kelolaan anda. Mengarahkan ke halaman customer
								</>
							),
							btn: (
								<Button
									type="primary"
									size="small"
									style={{ height: 32 }}
									onClick={async () => {
										notification.destroy(notificationKey);
										setTimeout(async () => {
											navigate('CustomerPage', {
												customerId: foundCustomerId,
											});

											await queryClient.refetchQueries({
												queryKey: ['getCustomerDetail', foundCustomerId],
											});
										}, 100);
									}}
								>
									Lanjutkan
								</Button>
							),
						});
						return;
					}
				}
				if (
					(items[0].source === 'ESTAR' &&
						items[0].createdById &&
						items[0].createdById !== userData?.userId) ||
					(items[0].source === 'ESTARLOS' &&
						items[0].createdById !== userData?.userId)
				) {
					notification.warning({
						key: notificationKey,
						message: 'Customer ditemukan',
						description: (
							<>
								Customer dengan NPWP <strong>{npwpNo}</strong> ditemukan dan
								merupakan kelolaan FRM lain, tidak bisa membuat customer dengan
								npwp ini
							</>
						),
					});
					return;
				}

				notification.success({
					key: notificationKey,
					message: 'Customer ditemukan',
					description: (
						<>
							Customer dengan NPWP <strong>{npwpNo}</strong> ditemukan.
							Lanjutkan untuk membuat dari existing customer dari ESTAR.
						</>
					),
					btn: (
						<>
							<Button
								type="primary"
								size="small"
								style={{ height: 32 }}
								onClick={() => {
									notification.destroy(notificationKey);

									setTimeout(async () => {
										navigate('CustomerPage', {
											customerId: foundCustomerId,
										});
										await queryClient.invalidateQueries({
											queryKey: ['getCustomerDetail'],
										});
										await queryClient.refetchQueries({
											queryKey: ['getCustomerDetail', foundCustomerId],
										});
									}, 100);
								}}
							>
								Lanjutkan
							</Button>
							<Button
								size="small"
								style={{ height: 32, marginLeft: 8 }}
								onClick={() => {
									notification.destroy(notificationKey);
								}}
							>
								Cancel
							</Button>
						</>
					),
					duration: 0,
				});
			} else {
				notification.info({
					message: 'Customer tidak ditemukan',
					description: `Customer dengan NPWP ${npwpNo} tidak ditemukan di sistem ESTAR. Silakan melanjutkan pembuatan customer baru`,
				});
				setIsCheckCustomer(true);
			}
		} catch (error) {
			notification.error({
				message: 'Error',
				description: 'Gagal melakukan pencarian customer. Silakan coba lagi.',
			});
			setIsCheckCustomer(false);
		}
	};

	return (
		<Fragment>
			{isEdit && !!alerts.length && (
				<Alert
					message={
						<>
							<ul>
								{alerts.map((alert, index) => (
									<li key={index}>{alert}</li>
								))}
							</ul>
						</>
					}
					type="warning"
					className="custom-alert"
					showIcon
					style={{ marginBottom: 20 }}
				/>
			)}

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Jenis Badan Usaha"
						rules={[rule.required('Jenis badan Usaha wajib diisi')]}
						name={fieldName(['customerInformation', 'businessEntity'])}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.value,
								},
							};
						}}
					>
						<Select {...selectBusinessEntity} />
					</Form.Item>
				</Col>
				{value?.customerInformation?.businessEntity?.id ===
					BusinessEntityType['Perseroan Terbatas'] && (
						<Col span={12}>
							<Form.Item
								label="Jenis Perusahaan"
								rules={[rule.required('Jenis perusahaan wajib diisi')]}
								name={fieldName(['customerInformation', 'companyType'])}
								getValueFromEvent={(_, option) => option?.data}
								getValueProps={(value) => {
									if (!value) return {};
									return {
										value: {
											value: value.id,
											label: value.value,
										},
									};
								}}
							>
								<Select {...selectCompanyType} />
							</Form.Item>
						</Col>
					)}
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Nama Customer"
						rules={[
							rule.required('Nama customer harus diisi'),
							rule.charOnly(),
						]}
						name={fieldName(['customerInformation', 'customerName'])}
						normalize={(value) => (value || '').toUpperCase()}
					>
						<Input disabled={isEdit} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Email Perusahaan"
						rules={[rule.required('Email harus diisi'), rule.email()]}
						name={fieldName(['customerInformation', 'companyEmail'])}
					>
						<Input type="text" />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="NPWP"
						name={fieldName(['customerInformation', 'npwpNo'])}
						{...formItem.npwp}
					/>
					{/* if npwpno length is 16, and custoemrId is null, then show a button "check customer" */}
					{(value?.customerInformation?.npwpNo?.length ?? 0) >= 15 &&
						!customerId ? (
						<Flex
							justify="end"
							style={{ marginTop: 10 }}
						>
							<Button
								type="primary"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleCheckCustomer(value?.customerInformation?.npwpNo || '');
								}}
							>
								Check Customer
							</Button>
						</Flex>
					) : null}
				</Col>
				<Col span={12}>
					<Form.Item
						label="Group Customer BMPP"
						name={fieldName(['customerInformation', 'companyGroup'])}
						getValueProps={(value) => ({
							value: value?.groupName ?? '',
						})}
					>
						<Input disabled />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Alamat Perusahaan"
						rules={[rule.required('Alamat perusahaan wajib diisi')]}
						name={fieldName(['customerInformation', 'companyAddress'])}
					>
						<Input.TextArea rows={3} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="RT/RW"
						required
					>
						<Row
							wrap={false}
							align="middle"
							gutter={8}
						>
							<Col flex="auto">
								<Form.Item
									rules={rules.rt}
									name={fieldName(['customerInformation', 'rt'])}
									noStyle
								>
									<Input {...inputRtRw} />
								</Form.Item>
							</Col>
							<Col flex="none">/</Col>
							<Col flex="auto">
								<Form.Item
									rules={rules.rw}
									name={fieldName(['customerInformation', 'rw'])}
									noStyle
								>
									<Input {...inputRtRw} />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Kode POS"
						rules={[rule.required('Kode POS wajib diisi')]}
						name={fieldName(['customerInformation', 'address'])}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.postalId,
									label: value.postalCode,
								},
							};
						}}
					>
						<Select {...selectAddress} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Kelurahan">
						<Input
							disabled
							value={value?.customerInformation?.address?.locationName}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item label="Kecamatan">
						<Input
							disabled
							value={value?.customerInformation?.address?.districtName}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Kota">
						<Input
							disabled
							value={value?.customerInformation?.address?.cityName}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName(['customerInformation', 'industrySector'])}
						label="Sektor Industri Level 5"
						rules={[rule.required('Sektor Industri Level 5 wajib diisi')]}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.sectorLevel5,
								},
							};
						}}
					>
						<Select
							{...selectIndustry}
							onSelect={(value, option) => {
								selectIndustry?.onSelect!(value, option);
								form.resetFields([
									fieldName(['customerInformation', 'deviation']),
								]);
							}}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Sektor Industri Level 4">
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{value?.customerInformation?.industrySector?.sectorLevel4 || '-'}
						</div>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item label="Sektor Industri Level 3">
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{value?.customerInformation?.industrySector?.sectorLevel3 || '-'}
						</div>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Sektor Industri Level 2">
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{value?.customerInformation?.industrySector?.sectorLevel2 || '-'}
						</div>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item label="Sektor Industri Level 1">
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{value?.customerInformation?.industrySector?.sectorLevel1 || '-'}
						</div>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Kelas Industri">
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{value?.customerInformation?.industrySector?.industryClass || '-'}
						</div>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item label="Ekosistem">
						<div style={{ fontSize: 16, marginTop: 10 }}>
							{value?.customerInformation?.industrySector?.ecosystem || '-'}
						</div>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Portofolio Guideline">
						{isPGWaspada || isPGSelective ? (
							<div
								style={{
									fontSize: 16,
									marginTop: 10,
									color: '#E10101',
									fontWeight: 600,
								}}
							>
								{value.customerInformation?.industrySector
									?.portofolioGuideline || '-'}
							</div>
						) : (
							<div style={{ fontSize: 16, marginTop: 10 }}>
								{value?.customerInformation?.industrySector
									?.portofolioGuideline || '-'}
							</div>
						)}
						{isPGWaspada && (
							<div
								style={{
									fontSize: 10,
									marginTop: 10,
									color: '#E10101',
									fontWeight: 400,
									background: '#FFE3E3',
									padding: '6px 6px',
								}}
							>
								Debitur termasuk dalam kategori{' '}
								<strong>&ldquo;WASPADA&rdquo;</strong> sehingga proses tidak
								dapat dilanjutkan
							</div>
						)}
						{isPGSelective && (
							<div
								style={{
									fontSize: 10,
									marginTop: 10,
									color: '#E10101',
									fontWeight: 400,
									background: '#FFE3E3',
									padding: '6px 6px',
								}}
							>
								Debitur termasuk dalam kategori{' '}
								<strong>&ldquo;SELEKTIF&rdquo;</strong> sehingga proses wajib
								mengajukan Nota Pengajuan Khusus. Silakan klik{' '}
								<a
									href="#"
									className="trigger-popup"
									onClick={() => setShowDeviationModal(true)}
								>
									Ajukan Penyimpangan
								</a>
							</div>
						)}
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Nama PIC Customer"
						rules={[rule.required('Nama PIC wajib diisi'), rule.charOnly()]}
						name={fieldName(['customerInformation', 'picName'])}
					>
						<Input />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Jabatan"
						rules={[rule.required('Jabatan wajib diisi')]}
						name={fieldName(['customerInformation', 'picPosition'])}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.value,
								},
							};
						}}
					>
						<Select {...selectPicPosition} />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Form.Item
				label="Informasi Tambahan"
				rules={[rule.required('Informasi tambahan wajib diisi')]}
				name={fieldName(['customerInformation', 'additionalInformation'])}
				labelCol={{ span: 6 }}
				wrapperCol={{
					style: {
						color: 'blue',
						marginLeft: -10,
					},
				}}
			>
				<Input.TextArea rows={3} />
			</Form.Item>

			<Button
				type="primary"
				disabled={
					isPGWaspada ||
					(isPGSelective && !isDeviationFilled) ||
					!isCheckCustomer
				}
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 60,
				}}
				onClick={async () => {
					// Validate NPWP specifically
					const npwpField = fieldName(['customerInformation', 'npwpNo']);
					try {
						await form.validateFields([npwpField]);
						await form.validateFields().then(() => {
							onSubmit();
						});
					} catch {
						// Validation failed, don't proceed
						return;
					}
					onSubmit();
				}}
			>
				NEXT
			</Button>

			<FleetModal
				open={showDeviationModal}
				onCancel={() => setShowDeviationModal(false)}
				width={940}
				okText="SAVE"
				cancelText="BACK"
				buttonPosition="end"
				closable={false}
				onOk={async () => {
					const isValid = await form.validateFields([
						fieldName(['customerInformation', 'deviation', 'background']),
						fieldName(['customerInformation', 'deviation', 'mitigation']),
						fieldName(['customerInformation', 'deviation', 'risk']),
					]);
					if (isValid) {
						setIsDeviationFilled(true);
						setShowDeviationModal(false);
					}
				}}
			>
				<FleetTitle level={4}>Add Pengajuan Kondisi Khusus</FleetTitle>
				<Form.Item
					style={{ marginTop: 20 }}
					name={fieldName(['customerInformation', 'deviation', 'background'])}
					label="Latar Belakang"
					labelCol={{ span: 6 }}
					rules={[rule.required()]}
				>
					<Input.TextArea />
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName(['customerInformation', 'deviation', 'mitigation'])}
					label="Mitigasi"
					labelCol={{ span: 6 }}
					rules={[rule.required()]}
				>
					<Input.TextArea />
				</Form.Item>
				<Divider />
				<Form.Item
					name={fieldName(['customerInformation', 'deviation', 'risk'])}
					label="Risiko"
					labelCol={{ span: 6 }}
					rules={[rule.required()]}
				>
					<Input.TextArea />
				</Form.Item>
			</FleetModal>
		</Fragment>
	);
};

export default FRMCustomerData;
